import { useState, useEffect } from 'react'
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom'
import { loginSchema } from '@hirebridge/validation'
import { ApiClientError } from '@hirebridge/api-client'
import { useAuth } from '../lib/auth'
import { GoogleSignInButton } from '../components/GoogleSignInButton'

export function LoginPage() {
  const { user, loading, login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Determine return target path from state.from or ?redirect query param
  const fromLocation = (location.state as any)?.from?.pathname
  const redirectParam = searchParams.get('redirect')
  const returnTarget = fromLocation || redirectParam
  const loggedOutNotice = (location.state as any)?.loggedOut

  // Helper for dynamic dashboard routing based on authenticated user
  const resolveDashboard = (targetUser: any) => {
    if (returnTarget) return returnTarget
    if (targetUser?.companies && targetUser.companies.length > 0) {
      return '/employer'
    }
    if (targetUser?.roles && (targetUser.roles.includes('employer') || targetUser.roles.includes('owner') || targetUser.roles.includes('admin') || targetUser.roles.includes('recruiter'))) {
      return '/employer'
    }
    return '/candidate'
  }

  // Auto-session check: if user already has an active session, auto-redirect to dashboard
  useEffect(() => {
    if (!loading && user) {
      navigate(resolveDashboard(user), { replace: true })
    }
  }, [user, loading])

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)

    const parsed = loginSchema.safeParse({ email, password })

    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Please check your login details.')
      return
    }

    setBusy(true)

    try {
      const loggedInUser = await login(email, password)
      const target = resolveDashboard(loggedInUser)
      navigate(target)
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Invalid credentials. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  if (loading) {
    return (
      <div className="auth-wrapper" style={{ minHeight: 'calc(100vh - 120px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="page-state">Checking session status…</div>
      </div>
    )
  }

  return (
    <div className="auth-wrapper" style={{ minHeight: 'calc(100vh - 120px)', padding: '2rem 1rem' }}>
      <div className="auth-card" style={{ maxWidth: '480px' }}>
        <div className="auth-header">
          <div className="brand-icon auth-modal-brand-icon" style={{ padding: '4px', background: 'transparent' }}>
            <img src="/logo-icon.png" alt="FibonacciPi" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          </div>
          <h1>Welcome to FibonacciPi</h1>
          <p>Sign in to manage your jobs, applications, and team</p>
        </div>

        {loggedOutNotice && !error && (
          <div className="alert-box alert-success auth-alert" style={{ background: '#f0fdf4', borderColor: '#bbf7d0', color: '#166534' }}>
            <span>✓</span> You have been signed out successfully.
          </div>
        )}

        {error && (
          <div className="alert-box alert-error auth-alert" role="alert">
            <span>⚠️</span> {error}
          </div>
        )}

        {/* Google OAuth 1-Click Sign In */}
        <GoogleSignInButton
          mode="signin"
          onSuccess={() => {
            if (returnTarget) navigate(returnTarget)
            else navigate('/candidate')
          }}
          onError={(err) => setError(err)}
        />

        <div className="auth-divider">
          <span>or continue with email</span>
        </div>

        <form onSubmit={submit} className="auth-form" noValidate>
          <div className="form-group">
            <label htmlFor="email">Email or Phone</label>
            <div className="input-with-icon">
              <span className="input-icon">✉️</span>
              <input
                id="email"
                type="text"
                autoComplete="username"
                placeholder="e.g. asha@acme.test or ravi@example.test"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.375rem' }}>
              <label htmlFor="password" style={{ margin: 0 }}>Password</label>
              <Link
                to="/forgot-password"
                className="auth-switch-link"
                style={{ fontSize: '0.8125rem', fontWeight: 600 }}
              >
                Forgot password?
              </Link>
            </div>
            <div className="input-with-icon">
              <span className="input-icon">🔒</span>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowPassword(!showPassword)}
                title={showPassword ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          <button type="submit" className="btn-auth-submit" disabled={busy}>
            {busy ? (
              <span className="btn-busy-content">
                <span className="spinner-inline"></span> Signing in…
              </span>
            ) : (
              'Sign In to Account'
            )}
          </button>
        </form>

        <div className="auth-modal-footer" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', textAlign: 'center' }}>
          <div>
            Don't have an account?{' '}
            <Link to="/register" className="auth-switch-link" style={{ fontWeight: 700 }}>
              Create free account
            </Link>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', fontSize: '0.8125rem', color: '#64748b' }}>
            <Link to="/register?role=candidate" style={{ color: '#2563eb' }}>
              🎯 Register as Job Seeker
            </Link>
            <span>•</span>
            <Link to="/register?role=employer" style={{ color: '#2563eb' }}>
              💼 Register as Employer
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}